import { networkErrorCodesFor } from "@getpaseo/protocol/network-error-codes";
// The one failure taxonomy for server, relay and UI (knowledge/123 rule 1); dependency-free.
// Closed set; only "fault" becomes an Error Tracking issue. Unknown is a fault.
export const FAILURE_KINDS = ["fault", "refusal", "transient", "expected", "recovered"];
export function parseFailureKind(value) {
    return FAILURE_KINDS.find((kind) => kind === value) ?? "fault";
}
export const PROVIDER_PROBE_TIMEOUT_CODE = "provider_probe_timeout";
export const DAEMON_OFFLINE_CODE = "daemon_offline";
// Wire codes that answer a caller's request with "no" rather than report a fault.
export const REFUSAL_CODES = new Set([
    "switch_account_unknown",
    "switch_not_supported",
    "switch_in_progress",
    "forbidden",
    "not_owner",
    "refused",
    "delegated_read_refused",
    "not_found",
    "unauthenticated",
    "unauthorized",
    "not_authorized",
    "not_a_member",
    "cross_workspace",
    "no_daemon_access",
    "conflict",
    "invalid",
    "invalid_request",
    "validation_error",
    "rate_limited",
    "already_deleted",
    "stale_send",
    "timeline_rewinding",
    "not_configured",
    "not_available",
    "unsupported",
    "unsupported_provider",
    "fetch_recent_provider_sessions_forbidden",
    "license_required",
    "upgrade_required",
    "pro_required",
    "seat_limit_reached",
    "attachment_inline_rejected",
    "attachment_invalid",
    "invalid_emoji",
    "invite_not_pending",
    "relay_claim_declined",
    "relay_host_not_allowed",
    "daemon_owned_by_other_account",
    "workspace_not_ready",
    // Integrations: nothing linked, link owned elsewhere, token needs reconnect, tenant policy, project synced elsewhere.
    "no_linked_channels",
    "link_owned_elsewhere",
    "link_owned_elsewhere_not_permitted",
    "cyborg_channel_already_bridged",
    "bridge_move_not_permitted",
    "bridge_move_requires_replace",
    "needs_reconnect",
    "team_owner_required",
    "install_forbidden",
    "consent_insufficient",
    "project_linked_to_other_provider",
    "pi_mcp_adapter_missing",
    "page_agent_busy",
    "page_agent_running",
    "page_agent_owner_cannot_read",
    "sign_in_missing",
    "prompt_channel_bound_session",
    // A prompt into a session that has ended (relay AGENT_SESSION_ENDED_MESSAGE): the session is gone.
    "session_ended",
    // An email-bound invitation opened while signed in to another account (relay INVITE_WRONG_ACCOUNT).
    "invite_wrong_account",
    // A channel name an active channel already holds (relay create/rename channel).
    "name_taken",
    // A model switch Claude Code did not apply (cyborg/claude-model-switch.ts).
    "model_unavailable",
    // A Composio toolkit its account's admin has not set up (cyborg/composio-connect.ts).
    "auth_config_missing",
    "oauth_app_required",
    // A pass the session ledger never listed (routes/auth.ts answers 409 and logs a ConflictError): the client refreshes and retries.
    "session_not_listed",
]);
// An infrastructure blip with a retry in flight: the machine is offline or slow, not broken.
export const TRANSIENT_CODES = new Set([
    "connection_lost",
    DAEMON_OFFLINE_CODE,
    "no_daemon",
    "daemon_timeout",
    "daemon_disconnected",
    PROVIDER_PROBE_TIMEOUT_CODE,
]);
// Node and undici codes for a peer or path that went away, never for anything this side built.
const TRANSPORT_ERROR_CODES = new Set(networkErrorCodesFor("transport"));
function codeOf(value) {
    return value !== null && typeof value === "object" ? Reflect.get(value, "code") : undefined;
}
/**
 * An outbound call's failure: transient for an upstream outage (a 5xx or 429 `status`, a
 * deadline `TimeoutError`/`AbortError`, a transport code on the error or its cause); anything
 * else (a 4xx, an invalid URL, a decode or programming error) is a fault.
 */
export function transportFailureKind(err) {
    const status = err !== null && typeof err === "object" ? Reflect.get(err, "status") : undefined;
    if (typeof status === "number")
        return status >= 500 || status === 429 ? "transient" : "fault";
    if (err instanceof Error && (err.name === "TimeoutError" || err.name === "AbortError")) {
        return "transient";
    }
    const code = codeOf(err) ?? (err instanceof Error ? codeOf(err.cause) : undefined);
    return typeof code === "string" && TRANSPORT_ERROR_CODES.has(code) ? "transient" : "fault";
}
export function failureKindForCode(code) {
    if (code && REFUSAL_CODES.has(code))
        return "refusal";
    if (code && TRANSIENT_CODES.has(code))
        return "transient";
    return "fault";
}
//# sourceMappingURL=index.js.map